import * as React from 'react'
import styled from 'styled-components'
import { NotificationData } from '../../../interfaces/Notifications'
import { animated, useSpring } from 'react-spring'
import ProfileImage from '../../profile/image'
import moment from 'moment'
import { useMediaQuery } from '@material-ui/core'
import { MarkNotificationSeen } from '../../../data/Notifications'
import { getShortenedAddress } from '../../../utils/address'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { useRouter } from 'next/router'
import useAuth from '../../../hooks/auth'

const OuterDiv = styled(animated.div)`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 15px;
  padding-left: 30px;
  cursor: pointer;
`

const InnerDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const EventDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 15px;
`

const EndDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 15px;
`

const Circle = styled.div<{ visible: boolean }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  background-color: rgb(10, 132, 255);
`

const Title = styled(animated.div)<{ neutral: boolean }>`
  color: ${(props) => (props.neutral ? 'white' : 'rgb(48, 209, 88)')};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  white-space: pre-wrap;
  text-align: left;
  margin-right: 7.5px;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;

  @media (min-width: 43em) {
    font-size: 14px;
  }
`

const Subtitle = styled.div<{ unread: boolean }>`
  color: ${(props) => (props.unread ? 'rgb(10, 132, 255)' : '#cdcdcd')};
  font-size: 14px;
  font-weight: ${(props) => (props.unread ? 600 : 500)};

  @media (min-width: 43em) {
    font-size: 12px;
  }
`

const StartDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Divider = styled.div`
  width: 100%;
  background: #323232;
  height: 1px;
  margin-top: 15px;
`

const ProfileWrapper = styled.div<{ width: string }>`
  min-width: ${(props) => props.width};
`

interface Props {
  notification: NotificationData
  close: () => void
}

const NotificationItem = ({ notification, close }: Props): JSX.Element => {
  const isMobile = useMediaQuery('(max-width: 43em)')
  const { profile } = useAuth()
  const [hover, setHover] = React.useState(false)
  const router = useRouter()

  const hoverState = useSpring({
    background: hover ? '#323232' : '#101010',
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  const getNotificationText = (): string => {
    if (notification.activity.verb === 'like' && notification.activity.content !== '') {
      return `${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.actor)
      } liked your comment: "${notification.activity.content}"`
    } else if (notification.activity.verb === 'like') {
      return `${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.actor)
      } liked your video.`
    } else if (notification.activity.verb === 'reply') {
      return `${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.actor)
      } replied to your comment: "${notification.activity.content}"`
    } else if (notification.activity.verb === 'comment') {
      return `${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.actor)
      } commented on your video: "${notification.activity.content}"`
    } else if (notification.activity.verb === 'follow') {
      return `${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.actor)
      } started following you.`
    } else if (
      notification.activity.verb === 'sold_secondary' ||
      notification.activity.verb === 'sold_primary'
    ) {
      return `You just sold your video to ${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.extra.buyer)
      } for ${
        Math.round(
          (100 * Number(notification.activity.extra.price.toString())) / LAMPORTS_PER_SOL
        ) / 100
      } SOL.`
    } else if (
      notification.activity.verb === 'bought_secondary' ||
      notification.activity.verb === 'bought_primary'
    ) {
      return `You just bought a video from ${
        notification.user?.displayName ?? getShortenedAddress(notification.activity.extra.seller)
      } for ${
        Math.round(
          (100 * Number(notification.activity.extra.price.toString())) / LAMPORTS_PER_SOL
        ) / 100
      } SOL.`
    } else {
      return ``
    }
  }

  const getLink = (): string => {
    if (notification.activity.verb === 'follow') {
      return `/${notification.activity.actor}`
    } else if (
      notification.activity.verb === 'sold_secondary' ||
      notification.activity.verb === 'bought_secondary' ||
      notification.activity.verb === 'bought_primary' ||
      notification.activity.verb === 'sold_primary'
    ) {
      return `/watch/${notification.activity.extra.mint}`
    } else {
      return `/watch/${notification.address}`
    }
  }

  const markSeen = async (): Promise<void> => {
    router.push(getLink())
    close()
    await MarkNotificationSeen(profile?.address ?? '', notification.activity.group)
  }

  const viewProfile = (event: React.MouseEvent, address: string): void => {
    event.stopPropagation()
    router.push(`/${address}`)
    close()
  }

  return (
    <OuterDiv
      style={hoverState}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={markSeen}
    >
      <InnerDiv>
        <StartDiv>
          <ProfileWrapper
            onClick={(e) => viewProfile(e, notification.user?.address ?? '')}
            width={isMobile ? '40px' : '50px'}
          >
            <ProfileImage item={notification.user} size={isMobile ? 40 : 50} />
          </ProfileWrapper>

          <EventDiv>
            <Title neutral={true}>{getNotificationText()}</Title>
            <Subtitle unread={!notification.activity.is_group_seen}>{`${moment(
              new Date(notification.activity.time * 1000)
            ).fromNow()}`}</Subtitle>
          </EventDiv>
        </StartDiv>
        <EndDiv>
          <Circle visible={!notification.activity.is_group_seen} />
        </EndDiv>
      </InnerDiv>
      <Divider />
    </OuterDiv>
  )
}

export default NotificationItem
